import { easeOut } from './cubic-bezier';
import { keyframes } from './keyframes';

/**
 * Keyframe animations shared by the built-in sets, as the approved mockup defines them
 * (`video-sets-spec.html`, `@keyframes pop / slam / shake`). `t` — seconds from the scene start.
 */

/** `pop`: scale 0 → 1.14 at 65 % → 1, each segment `ease-out`. */
export function popScale(t: number, delaySec: number, durationSec: number): number {
  return keyframes(t, [
    { atSec: delaySec, value: 0, ease: easeOut },
    { atSec: delaySec + 0.65 * durationSec, value: 1.14, ease: easeOut },
    { atSec: delaySec + durationSec, value: 1 },
  ]);
}

/** `slam`: scale 1.7 → .94 at 55 % → 1, opacity 0 → 1 by 55 %, each segment `ease-out`. */
export function slam(t: number, delaySec: number, durationSec: number): { scale: number; alpha: number } {
  const mid = delaySec + 0.55 * durationSec;
  const end = delaySec + durationSec;
  return {
    scale: keyframes(t, [
      { atSec: delaySec, value: 1.7, ease: easeOut },
      { atSec: mid, value: 0.94, ease: easeOut },
      { atSec: end, value: 1 },
    ]),
    alpha: keyframes(t, [
      { atSec: delaySec, value: 0, ease: easeOut },
      { atSec: mid, value: 1 },
    ]),
  };
}

/** `shake` .32 s linear: (0,0) → (−14,8) → (11,−7) → (−7,5) → (4,−2) → (0,0) at every 20 %. */
const SHAKE_SEC = 0.32;
const SHAKE_X = [0, -14, 11, -7, 4, 0];
const SHAKE_Y = [0, 8, -7, 5, -2, 0];

export function shakeOffset(t: number, delaySec: number): { dx: number; dy: number } {
  const at = (values: number[]) =>
    keyframes(
      t,
      values.map((value, i) => ({ atSec: delaySec + (i / (values.length - 1)) * SHAKE_SEC, value })),
    );
  return { dx: at(SHAKE_X), dy: at(SHAKE_Y) };
}

/** Opacity of a CSS animation leaving 0…1 on an overshooting curve: the browser clamps it. */
export const clampAlpha = (value: number) => Math.min(1, Math.max(0, value));
